import { useState, useCallback, useRef, useEffect, forwardRef } from 'react';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Dialog from '@mui/material/Dialog';
import AppBar from '@mui/material/AppBar';
import Toolbar from '@mui/material/Toolbar';
import IconButton from '@mui/material/IconButton';
import TextField from '@mui/material/TextField';
import CircularProgress from '@mui/material/CircularProgress';
import Typography from '@mui/material/Typography';
import Stack from '@mui/material/Stack';
import Chip from '@mui/material/Chip';
import Slide from '@mui/material/Slide';
import MyLocationIcon from '@mui/icons-material/MyLocation';
import MapOutlinedIcon from '@mui/icons-material/MapOutlined';
import CloseIcon from '@mui/icons-material/Close';
import PlaceOutlinedIcon from '@mui/icons-material/PlaceOutlined';
import SearchIcon from '@mui/icons-material/Search';
import { useJsApiLoader, GoogleMap, Marker, Autocomplete } from '@react-google-maps/api';
import { useGetAppSettingsQuery } from '../../store/siteContentApi';

const GOOGLE_LIBRARIES = ['places'];
// Maps key now flows through AppSettings (admin-editable). Env var
// stays as a build-time fallback so a fresh dev environment without
// AppSettings populated still loads the map. Single canonical name —
// REACT_APP_GOOGLE_MAPS_API_KEY — used everywhere (this picker used
// to read REACT_APP_GOOGLE_MAPS_KEY which silently broke in prod).
const ENV_KEY = process.env.REACT_APP_GOOGLE_MAPS_API_KEY;

const DEFAULT_CENTER = { lat: 17.4399, lng: 78.4983 }; // Hyderabad

// Walks the address_components[] from a geocoder/place result and returns
// flat parts useful for forms, navigation, and analytics.
const extractAddressParts = (components = []) => {
  const get = (type, useShort = false) => {
    const c = components.find((x) => x.types?.includes(type));
    if (!c) return '';
    return useShort ? c.short_name : c.long_name;
  };
  return {
    street_number: get('street_number'),
    street: get('route'),
    sublocality: get('sublocality_level_1') || get('sublocality') || get('neighborhood'),
    area: get('sublocality_level_2') || get('administrative_area_level_3'),
    city: get('locality') || get('administrative_area_level_2'),
    district: get('administrative_area_level_2'),
    state: get('administrative_area_level_1'),
    country: get('country'),
    country_code: get('country', true),
    pincode: get('postal_code'),
  };
};

// Builds the human-readable string that goes into the textarea, ordered:
// "street_number street, sublocality, area, city, district, state, country".
// Dedupes consecutive duplicates (city often === district from Google).
const composeAddress = (parts = {}) => {
  const street = [parts.street_number, parts.street].filter(Boolean).join(' ');
  const segs = [
    street,
    parts.sublocality,
    parts.area,
    parts.city,
    parts.district,
    parts.state,
    parts.country,
  ]
    .map((s) => (s || '').trim())
    .filter(Boolean);
  return segs.filter((s, i) => s.toLowerCase() !== (segs[i - 1] || '').toLowerCase()).join(', ');
};

const reverseGeocode = (lat, lng) =>
  new Promise((resolve) => {
    const g = window.google;
    if (!g?.maps?.Geocoder) return resolve(null);
    new g.maps.Geocoder().geocode({ location: { lat, lng } }, (results, status) => {
      if (status === 'OK' && results?.[0]) {
        const parts = extractAddressParts(results[0].address_components);
        resolve({
          formatted_address: composeAddress(parts) || results[0].formatted_address,
          place_id: results[0].place_id,
          parts,
        });
      } else {
        resolve(null);
      }
    });
  });

const SlideUp = forwardRef(function Transition(props, ref) {
  return <Slide direction="up" ref={ref} {...props} />;
});

/**
 * Inner — receives a guaranteed non-empty `apiKey` from the wrapper.
 * Splitting the wrapper out fixes a real bug: useJsApiLoader injects
 * the Google Maps <script> tag on first render and de-dupes by id, so
 * if the very first render has an empty key (which happens before
 * useGetAppSettingsQuery resolves), the script loads in "for development
 * purposes only" mode and stays that way for the rest of the session.
 *
 * value: { address, lat?, lng?, accuracy?, place_id?, ...parts }
 * onChange(next)
 */
const LocationPickerInner = ({ apiKey, value, onChange, label = 'Collection Address *', placeholder }) => {
  const { isLoaded, loadError } = useJsApiLoader({
    googleMapsApiKey: apiKey,
    libraries: GOOGLE_LIBRARIES,
  });

  const [mapOpen, setMapOpen] = useState(false);
  const [picking, setPicking] = useState(null);
  const [gpsLoading, setGpsLoading] = useState(false);
  const [gpsError, setGpsError] = useState('');
  const autocompleteRef = useRef(null);
  const mapRef = useRef(null);

  // Hydrate the dialog session from current value when it opens
  useEffect(() => {
    if (mapOpen) {
      setPicking({
        lat: value?.lat ?? DEFAULT_CENTER.lat,
        lng: value?.lng ?? DEFAULT_CENTER.lng,
        address: value?.address || '',
        place_id: value?.place_id || null,
        parts: {
          street_number: value?.street_number || '',
          street: value?.street || '',
          sublocality: value?.sublocality || '',
          area: value?.area || '',
          city: value?.city || '',
          district: value?.district || '',
          state: value?.state || '',
          country: value?.country || '',
          country_code: value?.country_code || '',
          pincode: value?.pincode || '',
        },
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [mapOpen]);

  // ── "Use current location" (works without opening the map) ──
  const useCurrentLocation = useCallback(() => {
    if (!('geolocation' in navigator)) {
      setGpsError('Geolocation not supported in this browser');
      return;
    }
    setGpsError('');
    setGpsLoading(true);
    navigator.geolocation.getCurrentPosition(
      async (pos) => {
        const { latitude: lat, longitude: lng, accuracy } = pos.coords;
        const geo = await reverseGeocode(lat, lng);
        onChange({
          ...value,
          address: geo?.formatted_address || value?.address || `${lat.toFixed(5)}, ${lng.toFixed(5)}`,
          lat, lng, accuracy,
          place_id: geo?.place_id || null,
          ...(geo?.parts || {}),
        });
        setGpsLoading(false);
      },
      (err) => {
        setGpsLoading(false);
        const msg =
          err.code === 1 ? 'Location permission denied — please allow it in your browser'
          : err.code === 2 ? 'Could not detect location'
          : 'Location request timed out';
        setGpsError(msg);
      },
      { enableHighAccuracy: true, timeout: 10000, maximumAge: 0 }
    );
  }, [value, onChange]);

  // ── Inside the dialog ──
  const onMapLoad = useCallback((map) => { mapRef.current = map; }, []);

  const updatePickFromCoords = useCallback(async (lat, lng) => {
    const geo = await reverseGeocode(lat, lng);
    setPicking({
      lat, lng,
      address: geo?.formatted_address || `${lat.toFixed(5)}, ${lng.toFixed(5)}`,
      place_id: geo?.place_id || null,
      parts: geo?.parts || {},
    });
  }, []);

  const onMarkerDragEnd = useCallback((e) => {
    updatePickFromCoords(e.latLng.lat(), e.latLng.lng());
  }, [updatePickFromCoords]);

  const onMapClick = useCallback((e) => {
    updatePickFromCoords(e.latLng.lat(), e.latLng.lng());
  }, [updatePickFromCoords]);

  const onAutocompleteLoad = (ac) => { autocompleteRef.current = ac; };

  const onPlaceChanged = useCallback(() => {
    const place = autocompleteRef.current?.getPlace();
    if (!place?.geometry?.location) return;
    const lat = place.geometry.location.lat();
    const lng = place.geometry.location.lng();
    const parts = extractAddressParts(place.address_components);
    setPicking({
      lat, lng,
      address: composeAddress(parts) || place.formatted_address || place.name || '',
      place_id: place.place_id || null,
      parts,
    });
    if (mapRef.current) {
      mapRef.current.panTo({ lat, lng });
      mapRef.current.setZoom(17);
    }
  }, []);

  const confirmDialog = () => {
    if (picking) {
      onChange({
        ...value,
        address: picking.address,
        lat: picking.lat,
        lng: picking.lng,
        place_id: picking.place_id,
        accuracy: null, // pin from map, not GPS
        ...(picking.parts || {}),
      });
    }
    setMapOpen(false);
  };

  // Pretty list of resolved parts for the chips below the textarea.
  const partsForDisplay = [
    value?.sublocality,
    value?.city || value?.district,
    value?.state,
    value?.pincode,
  ].filter(Boolean);

  return (
    <Box>
      <TextField
        label={label}
        fullWidth
        multiline
        rows={3}
        placeholder={placeholder || 'Auto-fills when you use the buttons below — or type manually'}
        value={value?.address || ''}
        onChange={(e) => onChange({ ...value, address: e.target.value })}
      />

      {/* Action chips */}
      <Stack direction="row" spacing={1.25} flexWrap="wrap" sx={{ mt: 1.25, rowGap: 1, alignItems: 'center' }}>
        <Button
          size="small" variant="outlined"
          startIcon={gpsLoading ? <CircularProgress size={14} /> : <MyLocationIcon sx={{ fontSize: 18 }} />}
          onClick={useCurrentLocation}
          disabled={gpsLoading}
          sx={{
            textTransform: 'none', fontWeight: 600, fontSize: 12.5,
            borderRadius: 100, borderColor: 'var(--sage-p)', color: 'var(--forest)',
            '&:hover': { borderColor: 'var(--sage)', bgcolor: 'var(--mint)' },
          }}
        >
          Use current location
        </Button>
        <Button
          size="small" variant="outlined"
          startIcon={<MapOutlinedIcon sx={{ fontSize: 18 }} />}
          onClick={() => setMapOpen(true)}
          disabled={!isLoaded && !loadError}
          sx={{
            textTransform: 'none', fontWeight: 600, fontSize: 12.5,
            borderRadius: 100, borderColor: 'var(--sage-p)', color: 'var(--forest)',
            '&:hover': { borderColor: 'var(--sage)', bgcolor: 'var(--mint)' },
          }}
        >
          Pick on map
        </Button>
        {value?.lat != null && value?.lng != null && (
          <Box
            sx={{
              display: 'inline-flex', alignItems: 'center', gap: 0.5,
              fontSize: 11.5, color: 'var(--sage)', fontWeight: 600,
              bgcolor: 'var(--mint)', borderRadius: 100, px: 1.25, py: 0.5,
            }}
          >
            <PlaceOutlinedIcon sx={{ fontSize: 14 }} />
            {value.lat.toFixed(5)}, {value.lng.toFixed(5)}
          </Box>
        )}
      </Stack>

      {/* Parsed address parts as chips */}
      {partsForDisplay.length > 0 && (
        <Stack direction="row" spacing={0.75} flexWrap="wrap" sx={{ mt: 1.25, rowGap: 0.75 }}>
          {partsForDisplay.map((p) => (
            <Chip
              key={p}
              label={p}
              size="small"
              sx={{
                bgcolor: '#fff',
                border: '1px solid var(--border-l)',
                color: 'var(--ink)',
                fontWeight: 600,
                fontSize: 11.5,
              }}
            />
          ))}
        </Stack>
      )}

      {gpsError && (
        <Typography sx={{ fontSize: 11.5, color: 'var(--red, #DC2626)', mt: 0.75 }}>
          {gpsError}
        </Typography>
      )}
      {loadError && (
        <Typography sx={{ fontSize: 11.5, color: 'var(--red, #DC2626)', mt: 0.75 }}>
          Map failed to load — check the API key or your network.
        </Typography>
      )}

      {/* ── Full-screen map dialog ── */}
      <Dialog
        fullScreen
        open={mapOpen}
        onClose={() => setMapOpen(false)}
        TransitionComponent={SlideUp}
      >
        <AppBar
          position="relative"
          elevation={0}
          sx={{ bgcolor: 'var(--forest)', color: '#fff' }}
        >
          <Toolbar sx={{ gap: 1.5 }}>
            <IconButton edge="start" color="inherit" onClick={() => setMapOpen(false)}>
              <CloseIcon />
            </IconButton>
            <Typography sx={{ fontSize: 16, fontWeight: 700, flex: 1 }}>
              Pin your collection address
            </Typography>
            <Button
              onClick={confirmDialog}
              disabled={!picking?.lat}
              sx={{
                bgcolor: 'var(--amber)', color: '#fff',
                borderRadius: 100, textTransform: 'none', fontWeight: 700, px: 2.5,
                '&:hover': { bgcolor: '#a36506' },
                '&.Mui-disabled': { bgcolor: 'rgba(255,255,255,0.12)', color: 'rgba(255,255,255,0.4)' },
              }}
            >
              Confirm location
            </Button>
          </Toolbar>
        </AppBar>

        {!isLoaded ? (
          <Box sx={{ flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
            <CircularProgress sx={{ color: 'var(--sage)' }} />
          </Box>
        ) : (
          <Box sx={{ position: 'relative', flex: 1, minHeight: 0 }}>
            {/* Search bar overlaying the map */}
            <Box
              sx={{
                position: 'absolute', top: 14,
                left: '50%', transform: 'translateX(-50%)',
                width: { xs: 'calc(100% - 28px)', sm: 480 },
                zIndex: 5,
              }}
            >
              <Autocomplete
                onLoad={onAutocompleteLoad}
                onPlaceChanged={onPlaceChanged}
                options={{
                  componentRestrictions: { country: 'in' },
                  fields: ['geometry', 'formatted_address', 'name', 'place_id', 'address_components'],
                }}
              >
                <TextField
                  fullWidth
                  size="small"
                  placeholder="Search address, landmark, locality…"
                  InputProps={{
                    startAdornment: (
                      <SearchIcon sx={{ fontSize: 20, color: 'var(--stone)', mr: 1 }} />
                    ),
                    sx: {
                      bgcolor: '#fff',
                      borderRadius: '12px',
                      boxShadow: '0 6px 18px rgba(0,0,0,0.18)',
                      fontSize: 14,
                    },
                  }}
                />
              </Autocomplete>
            </Box>

            <GoogleMap
              mapContainerStyle={{ width: '100%', height: '100%' }}
              center={picking ? { lat: picking.lat, lng: picking.lng } : DEFAULT_CENTER}
              zoom={picking?.lat ? 16 : 12}
              onLoad={onMapLoad}
              onClick={onMapClick}
              options={{
                streetViewControl: false,
                mapTypeControl: false,
                fullscreenControl: false,
                zoomControl: true,
              }}
            >
              {picking?.lat && (
                <Marker
                  position={{ lat: picking.lat, lng: picking.lng }}
                  draggable
                  onDragEnd={onMarkerDragEnd}
                />
              )}
            </GoogleMap>

            {/* Bottom address card */}
            <Box
              sx={{
                position: 'absolute', left: 14, right: 14, bottom: 14,
                bgcolor: '#fff',
                borderRadius: '14px',
                p: 2,
                boxShadow: '0 10px 28px rgba(0,0,0,0.18)',
                maxWidth: 560, mx: 'auto',
              }}
            >
              <Typography sx={{ fontSize: 10.5, fontWeight: 700, letterSpacing: '0.1em', color: 'var(--stone)', textTransform: 'uppercase' }}>
                Selected address
              </Typography>
              <Typography sx={{ fontSize: 13.5, color: 'var(--ink)', mt: 0.5, lineHeight: 1.5 }}>
                {picking?.address || 'Search above, tap on the map, or drag the pin to choose your address.'}
              </Typography>
              {picking?.parts && (
                <Stack direction="row" spacing={0.75} flexWrap="wrap" sx={{ mt: 1, rowGap: 0.75 }}>
                  {[picking.parts.sublocality, picking.parts.city, picking.parts.state, picking.parts.pincode]
                    .filter(Boolean)
                    .map((p) => (
                      <Chip
                        key={p}
                        label={p}
                        size="small"
                        sx={{
                          bgcolor: 'var(--mint)',
                          color: 'var(--forest)',
                          fontWeight: 700,
                          fontSize: 11,
                        }}
                      />
                    ))}
                </Stack>
              )}
            </Box>
          </Box>
        )}
      </Dialog>
    </Box>
  );
};

// Outer wrapper — resolves the Google Maps API key from AppSettings and
// only mounts the inner (which calls useJsApiLoader) once a non-empty
// key is available. While the key is loading, we render a passive read-
// only address field so the form layout stays stable and the user can
// still see what they previously entered.
const LocationPicker = (props) => {
  const { data: appSettings, isLoading, isError, error } = useGetAppSettingsQuery();
  // The frontend's siteContentApi has transformResponse: raw => raw.settings,
  // so `appSettings` IS the settings object — NOT { settings: {...} }.
  // (Admin's slice doesn't transform, which is why admin works at .settings.)
  const apiKey = appSettings?.google_maps_api_key || ENV_KEY || '';

  // Debug — drop into the browser console so we can see exactly which
  // step is failing on the user's end (RTK fetch, missing key, env fallback).
  if (process.env.NODE_ENV !== 'production') {
    // eslint-disable-next-line no-console
    console.log('[LocationPicker] maps key debug:', {
      isLoading,
      isError,
      error,
      hasAppSettings: !!appSettings,
      keyFromSettings: appSettings?.google_maps_api_key ? 'present' : 'missing',
      keyFromEnv: ENV_KEY ? 'present' : 'missing',
      resolvedKeyLen: apiKey.length,
    });
  }

  if (!apiKey) {
    return (
      <Box>
        <TextField
          label={props.label || 'Collection Address *'}
          value={props.value?.address || ''}
          placeholder={props.placeholder || 'Loading map…'}
          fullWidth
          multiline
          minRows={2}
          disabled
          InputProps={{
            startAdornment: (
              <Box sx={{ mr: 1, display: 'flex', alignItems: 'center' }}>
                <CircularProgress size={16} sx={{ color: 'var(--sage)' }} />
              </Box>
            ),
          }}
        />
      </Box>
    );
  }
  return <LocationPickerInner apiKey={apiKey} {...props} />;
};

export default LocationPicker;
